import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  AppBar,
  Toolbar,
  Switch,
  ListItemButton,
  FormControlLabel,
  CircularProgress,
  Badge,
} from "@mui/material";
import {
  Chat as ChatIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { format, isToday, isYesterday } from "date-fns";
import { useStore } from "../../context/StoreContext";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";

import { api } from "../../hooks/apiClient";
import Pagination from "@mui/material/Pagination";
import { useSnackbar } from "notistack";
import OrderMsgDialog from "./components/OrderMsgDialog";

import MailIcon from "@mui/icons-material/Mail";
import Messages from "./components/MessagesSide";

const ChatHome = () => {
  const { enqueueSnackbar } = useSnackbar();
  const messagesEndRef = useRef(null);
  const { selectedStore, isStoreSelected } = useStore();
  const storePhoneId = isStoreSelected && selectedStore.phoneID;
  const [orderDialog, setOrderDialog] = useState(false);
  const [chats, setChats] = useState([]);
  const [tempChats, setTempChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [msgLoading, setMsgLoading] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const [autoLoad, setAutoLoad] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    hasMore: false,
    total: 0,
    totalPages: 0,
  });
  const toggleOpenDialog = () => {
    setOrderDialog((prev) => !prev);
  };
  const handlePageChange = (event, newPage) => {
    getAllConversations(storePhoneId, newPage, pagination.limit);
    setLoadingMore(true);
  };
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return format(date, "HH:mm");
    } else if (isYesterday(date)) {
      return "Yesterday";
    } else {
      return format(date, "dd/MM/yyyy");
    }
  };
  const getAllConversations = useCallback(
    async (phoneId, page = 1, limit = 50) => {
      if (!phoneId) {
        enqueueSnackbar("Phone ID is required", { variant: "error" });
        return;
      }

      try {
        const response = await api.get(`whatsapp/conversations/${phoneId}`, {
          params: { page, limit },
        });

        const { chats: newChats, total, totalPages, hasMore } = response.data;

        if (page === 1) {
          setChats(newChats);
          setTempChats(newChats);
        } else {
          setChats((prevChats) => [...prevChats, ...newChats]);
          setTempChats((prevChats) => [...prevChats, ...newChats]);
        }

        setPagination((prev) => ({
          ...prev,
          page,
          total,
          totalPages,
          hasMore: hasMore !== undefined ? hasMore : page < totalPages,
        }));
      } catch (error) {
        console.error("Error fetching chats:", error);
        const errorMessage =
          error.response?.data?.error || "Failed to fetch chats";

        enqueueSnackbar(errorMessage, { variant: "error" });
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [enqueueSnackbar],
  );
  const getSearchConversation = async () => {
    if (!storePhoneId) {
      enqueueSnackbar("Phone ID is required", { variant: "error" });
      return;
    }

    try {
      const response = await api.get(
        `whatsapp/search/conversations/${storePhoneId}`,
        {
          params: { search: search },
        },
      );

      const { chats: newChats, total, totalPages, hasMore } = response.data;

      setChats(newChats);

      setPagination((prev) => ({
        ...prev,
        page: 1,
        total,
        totalPages,
        hasMore: hasMore,
      }));
    } catch (error) {
      console.error("Error fetching chats:", error);
      const errorMessage =
        error.response?.data?.error || "Failed to fetch chats";

      // enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  // const loadMoreChats = useCallback(async () => {
  //   if (!loadingMore && pagination.hasMore) {
  //     setLoadingMore(true);
  //     try {
  //       await getAllConversations(storePhoneId, pagination.page + 1, pagination.limit);
  //     } catch (error) {
  //       console.error("Error loading more chats:", error);
  //     } finally {
  //       setLoadingMore(false);
  //     }
  //   }
  // }, [
  //   loadingMore,
  //   pagination.hasMore,
  //   pagination.page,
  //   getAllConversations,
  //   storePhoneId,
  // ]);
  const refreshChats = () => {
    if (storePhoneId) {
      getAllConversations(storePhoneId, 1, pagination.limit);
      setLoading(true);
    }
  };
  useEffect(() => {
    if (storePhoneId) {
      setPagination((prev) => ({ ...prev, page: 1, hasMore: true }));
      getAllConversations(storePhoneId, 1, 50);
    }
  }, [selectedStore]);
  useEffect(() => {
    if (!selectedChat) return;
    setMsgLoading(true);
    const fetchMessages = async () => {
      try {
        const response = await api.get(`whatsapp/messages/${selectedChat.id}`, {
          params: { conversationId: selectedChat.id },
        });
        const updatedChat = {
          ...selectedChat,
          unreadCount: 0,
          Messages: response.data,
        };

        setSelectedChat(updatedChat);
        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.id === selectedChat.id ? updatedChat : chat,
          ),
        );
      } catch (err) {
        console.error("Failed to load messages:", err);
      } finally {
        setMsgLoading(false);
      }
    };

    fetchMessages();
  }, [selectedChat?.id]);

  // const shouldLoadMore = autoLoad && pagination.hasMore && !loadingMore;
  // const conditionalLoadMore = useCallback(() => {
  //   if (shouldLoadMore) {
  //     loadMoreChats();
  //   }
  // }, [shouldLoadMore, loadMoreChats]); // Fewer dependencies

  // const sentinelRef = useInfiniteScroll(
  //   conditionalLoadMore,
  //   pagination.hasMore,
  //   loadingMore,
  //   { threshold: 0.8 },
  // );
  const searchChat = async () => {
    if (!search.trim()) {
      setChats(tempChats);
      return;
    }

    getSearchConversation();
  };

  useEffect(() => {
    if (msgLoading) return;
    if (messagesEndRef.current && selectedChat?.Messages) {
      const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      };
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [selectedChat?.Messages]);

  return (
    <Box sx={{ height: "95vh", display: "flex", flexDirection: "column" }}>
      <OrderMsgDialog open={orderDialog} ToggleOpen={toggleOpenDialog} />
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense">
          <ChatIcon sx={{ mr: 2 }} onClick={toggleOpenDialog} />
          <Typography component="div" sx={{ flexGrow: 1 }}>
            Whatsapp Conversations{" "}
          </Typography>

          <IconButton onClick={refreshChats} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ flexGrow: 1, display: "flex", overflow: "hidden" }}>
        {/* Conversations List */}
        <Paper
          sx={{
            width: 450,
            display: "flex",
            flexDirection: "column",
            borderRight: 1,
            borderColor: "divider",
            borderRadius: 0,
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <TextField
              type="number"
              fullWidth
              size="small"
              placeholder="Search conversations..."
              variant="outlined"
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  searchChat();
                }
              }}
            />
          </Box>
          {/* conversation site */}
          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <List sx={{ py: 0 }}>
                {chats.map((chat) => (
                  <React.Fragment key={chat.id}>
                    <ListItemButton
                      button
                      selected={selectedChat?.id === chat.id}
                      onClick={() => setSelectedChat(chat)}
                    >
                      <ListItemAvatar>
                        <Badge badgeContent={chat.unreadCount} color="primary">
                          <MailIcon color="action" />
                        </Badge>
                        {/* <Avatar>
                          {chat.customerNumber
                            ? chat.customerNumber.charAt(5)
                            : "U"}
                        </Avatar> */}
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" noWrap>
                            {chat.customerNumber}
                          </Typography>
                        }
                        secondary={
                          chat.Messages && chat.Messages.length > 0 ? (
                            <Typography variant="body2" noWrap>
                              {chat.Messages[chat.Messages.length - 1].content}
                            </Typography>
                          ) : (
                            "No messages"
                          )
                        }
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                        }}
                      >
                        <Typography variant="caption" color="textSecondary">
                          {formatDate(chat.updatedAt)}
                        </Typography>
                        {chat.Messages && chat.Messages.length > 0 && (
                          <Chip
                            label={
                              chat.Messages[0].status_status ||
                              chat.Messages[0].status
                            }
                            size="small"
                            color={
                              chat.Messages[0].status_status === "read"
                                ? "success"
                                : "default"
                            }
                            sx={{ mt: 0.5, fontSize: "0.7rem" }}
                          />
                        )}
                      </Box>
                    </ListItemButton>
                    <Divider variant="inset" component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
            {!loading && chats.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 3,
                  color: "text.secondary",
                }}
              >
                <ChatIcon sx={{ fontSize: 60, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6">No conversations yet</Typography>
                <Typography variant="body2" align="center">
                  Start a conversation to see it here
                </Typography>
              </Box>
            )}
          </Box>
          {/* pagination buttons */}
          {loadingMore ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            pagination.hasMore && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                }}
              >
                {pagination.totalPages > 1 && (
                  <Pagination
                    color="primary"
                    count={pagination.totalPages}
                    page={pagination.page}
                    size="small"
                    onChange={handlePageChange}
                  />
                )}
              </Box>
            )
          )}
        </Paper>

        {/* Message View */}
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Messages
            setChats={setChats}
            selectedChat={selectedChat}
            setSelectedChat={setSelectedChat}
            storePhoneId={storePhoneId}
            msgLoading={msgLoading}
          />
        </Box>
      </Box>
    </Box>
  );
};
export default ChatHome;
